/**
 * Contrato de respuestas del test de idoneidad de Lift v1.
 *
 * A diferencia de Wio, la respuesta NO es un nombre de enum: es el ORDEN 1-based de
 * la opción elegida. El motor de wio.suitability.api indexa el peso por ese orden
 * (`Providers/Lift/V1/Questionnaire.cs`), así que añadir o quitar una opción del
 * catálogo de textos sin tocar los pesos desplaza los índices y el cálculo puntúa
 * otra opción. Hay un test de contrato en la API que lo impide.
 *
 * Las listas por bloque son la fuente de verdad de este módulo: gobiernan qué
 * preguntas se pintan en cada paso y qué exige la puerta de completitud. Van en
 * orden numérico, como el Excel de la gestora.
 */

/** Bloque 1 — conocimientos y experiencia inversora. */
export const BLOCK1_QUESTIONS = [
  'q1',
  'q2',
  'q3',
  'q4',
  'q5',
  'q6',
  'q7',
  'q8',
  'q9',
  'q10',
  'q11',
  'q12',
] as const;

/** Bloque 2 — situación financiera. */
export const BLOCK2_QUESTIONS = [
  'q13',
  'q14',
  'q15',
  'q16',
  'q17',
  'q18',
  'q19',
  'q20',
] as const;

/** Bloque 3 — objetivos de inversión. Es el único con reglas (Q21 → Q22). */
export const BLOCK3_QUESTIONS = ['q21', 'q22', 'q23', 'q24', 'q25'] as const;

export type ScoredQuestionKey =
  | (typeof BLOCK1_QUESTIONS)[number]
  | (typeof BLOCK2_QUESTIONS)[number]
  | (typeof BLOCK3_QUESTIONS)[number];

/**
 * Bloque de sostenibilidad (S1–S6). No puntúa, es OPCIONAL y es el único con árbol
 * condicional; también el único cuyas respuestas no son todas un orden de opción.
 *
 * Espeja `SustainabilityAnswers` de la API campo a campo, incluido el nombre en
 * minúsculas: viaja anidado bajo `sustainability` y se serializa en camelCase.
 */
export interface LiftSustainabilityAnswers {
  /** S1 · 1 neutro · 2 delego en la entidad · 3 quiero determinar tipología y proporción. */
  s1?: number;
  /** S2 · porcentaje de la cartera, 0–100. NO es un orden de opción. */
  s2?: number;
  /** S3 · características con preferencias, multiselección: 1 taxonomía · 2 SFDR · 3 PIAS. */
  s3?: number[];
  /** S4 · alineación con la taxonomía. Sólo si S3 incluye taxonomía. */
  s4?: number;
  /** S5 · ídem según SFDR. Sólo si S3 incluye SFDR. */
  s5?: number;
  /** S6 · indicadores adversos, multiselección: 1 ambientales · 2 sociales. Sólo si S3 incluye PIAS. */
  s6?: number[];
}

/** S1 · la única opción que abre el resto del bloque. */
export const S1_SELF_DETERMINED_ORDER = 3;

/** S3 · qué característica abre cada condicional. */
export const S3_TAXONOMY_ORDER = 1;

export const S3_SFDR_ORDER = 2;

export const S3_ADVERSE_IMPACT_ORDER = 3;

/** S2 · rango del porcentaje, el mismo que valida el motor. */
export const S2_MIN_PERCENTAGE = 0;

export const S2_MAX_PERCENTAGE = 100;

/**
 * Respuestas del cuestionario: el orden elegido en cada pregunta. Todas opcionales
 * —el wizard tolera huecos mientras se rellena— y son las puertas de cada paso las
 * que exigen las 25 antes de dejar avanzar.
 *
 * La sostenibilidad va **anidada**, no aplanada, porque así la espera el motor. Ojo
 * con `onUpdate`, que hace merge superficial: parchear `sustainability` REEMPLAZA el
 * objeto entero, así que el paso tiene que esparcir lo que ya había.
 */
export type LiftAnswers = Partial<Record<ScoredQuestionKey, number>> & {
  sustainability?: LiftSustainabilityAnswers;
};
